import {
  OrganizationType,
  rdbV1FiltersYearsList,
  rdbV1OrganizationsRosterDetail,
  type RdbV1OrganizationsRosterDetailParams,
} from '@on3/api';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { getIdFromParam, getSportKeyFromParam } from '@on3/ui-lib/utils/url';
import {
  HighSchoolRosterPage,
  type IHighSchoolPageProps,
} from '@public/components/HighSchool/Roster/HighSchoolRosterPage';
import { type AxiosError } from 'axios';
import { type GetServerSideProps } from 'next';

const HighSchoolRosterPageWithYear = (props: IHighSchoolPageProps) => {
  return <HighSchoolRosterPage {...props} />;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const {
    year,
    sport = 'football',
    id,
  } = ctx.query as { year: string; id: string; sport: string };

  const orgKey = getIdFromParam(id);
  const sportKey = getSportKeyFromParam(sport);

  if (!siteData || !orgKey || !sportKey) {
    return {
      notFound: true,
    };
  }

  const defaultYear = siteData.defaultYears.highSchoolYear;

  const paramYear = year ? +year : defaultYear;

  if (paramYear === defaultYear) {
    return {
      redirect: {
        permanent: true,
        destination: `/high-school/${id}/${sport}/roster/`,
      },
    };
  }

  const params: RdbV1OrganizationsRosterDetailParams = {
    sportKey,
    year: paramYear,
    organizationKey: orgKey,
  };

  try {
    const [highSchoolRoster, years] = await Promise.allSettled([
      rdbV1OrganizationsRosterDetail(proxyApi, params),
      rdbV1FiltersYearsList(proxyApi),
    ]);
    const rosterData = getFulfilledValue(highSchoolRoster);

    const { relatedModel } = rosterData || {};

    const orgType = relatedModel?.orgResponse?.orgTypeEnum;
    const orgSlug = relatedModel?.orgResponse?.urlSlug;

    if (orgType !== OrganizationType.HighSchool) {
      return { notFound: true };
    }

    if (id !== orgSlug) {
      return {
        redirect: {
          permanent: true,
          destination: `/high-school/${orgSlug}/${sport}/${year}/roster/`,
        },
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || [],
      highSchoolRoster: rosterData,
      filters: {
        years: getFulfilledValue(years),
        sports: baseSports,
      },
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.error(err);

    return { props: {} };
  }
};

export default withValidUrl(HighSchoolRosterPageWithYear);
